import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as ChargingStationIcon } from '../../assets/icons/sidebar/sidebar-station.svg';
import {
  ChargingStationInterFace,
  RequestChargingStationInterface,
} from '../../interface/charging-station.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAllStation } from '../../redux/slices/ApprovedStationManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import { fetchAllRejectedStation } from '../../redux/slices/RejectedStationManagementSlice';
import { fetchUnApprivedElectricPendingCount } from '../../redux/slices/UnapprovedElectricityPriceSlice';
import {
  fetchAllUnapprovedStation,
  fetchPendingNotification,
} from '../../redux/slices/UnapprovedStationManagementSlice';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import TabMenus from '../../shared-components/tab-menus/TabMenus';
import chargingStationApi from '../../utils/api/charging-station';

import ApprovedListing from './components/ApprovedListing';
import RejectedListing from './components/RejectedListing';
import UnApprovedListing from './components/UnapprovedListing';
import ViewChargingStation from './components/view-charging-station';

/**
 * Charging Station Management function component
 */
function ChargingStationManagement() {
  const { t } = useTranslation('station');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const [searchParams, setSearchParams] = useSearchParams();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { currentPage } = useParams<{ currentPage: string }>();
  const { pendingNotificationCount } = useAppSelector((state) => state.unapprovedStation);
  const { pendingElectricityCount } = useAppSelector((state) => state.unapprovedElectricityPrice);
  const {
    totalResults,
    limit: approvedLimit,
    page: approvedPage,
    loading: approvedLoading,
  } = useAppSelector((state) => state.approvedStation);
  const {
    totalResults: unapprovedTotalResults,
    limit: unapprovedLimit,
    page: unapprovedPage,
    loading: unapprovedLoading,
  } = useAppSelector((state) => state.unapprovedStation);
  const {
    totalResults: rejectedTotalResults,
    limit: rejectedLimit,
    page: rejectedPage,
    loading: rejectedLoading,
  } = useAppSelector((state) => state.rejectedStation);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [openTab, setOpenTab] = useState<number>(0);
  const [currentPages, setCurrentPages] = useState<string>('station');
  const [modalView, setModalView] = useState({
    title: t('addAccount'),
    header: true,
    content: <>vds</>,
    rightCorner: '',
    notification: false,
  });

  /**
   * Close modal box function
   */
  const closeModal = () => {
    setOpenStatus(false);
  };

  /**
   * View charging station modal box function
   */
  const viewFunction = (currentData: ChargingStationInterFace) => {
    setOpenStatus(true);
    setModalView({
      title: t('addAccount'),
      header: false,
      content: <ViewChargingStation details={currentData} closeModal={closeModal} />,
      rightCorner: '',
      notification: false,
    });
  };
  /**
   * Fetches the list of approved charging stations from the API.
   * @param data Optional request parameters for fetching approved stations.
   */
  const fetchApprovedStations = (data?: RequestChargingStationInterface) => {
    if (!approvedLoading) {
      dispatch(fetchAllStation(data || { page: approvedPage, limit: approvedLimit }));
    }
  };
  /**
   * Fetches the list of unapproved charging stations from the API.
   * @param data Optional request parameters for fetching unapproved stations.
   */
  const fetchUnapprovedStations = (data?: RequestChargingStationInterface) => {
    if (!unapprovedLoading) {
      dispatch(fetchAllUnapprovedStation(data || { page: unapprovedPage, limit: unapprovedLimit }));
    }
  };
  /**
   * Fetches the list of rejected charging stations from the API.
   * @param data Optional request parameters for fetching rejected stations.
   */
  const fetchRejectedStations = (data?: RequestChargingStationInterface) => {
    if (!rejectedLoading) {
      dispatch(fetchAllRejectedStation(data || { page: rejectedPage, limit: rejectedLimit }));
    }
  };

  /**
   * Show success notification modal
   */
  const showSuccessNotification = () => {
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      rightCorner: '',
      notification: true,
      content: (
        <SuccessNotification
          title={t('stationDeletedSuccessfully')}
          description={t('stationDeletedWithChargingPoints')}
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setOpenStatus(false);
            dispatch(fetchPendingNotification());
            if (openTab === 0) fetchApprovedStations();
            else if (openTab === 1) fetchUnapprovedStations();
            else if (openTab === 2) fetchRejectedStations();
          }}
        />
      ),
    });
  };

  /**
   * Confirm Delete Station API Call
   * @param rowId
   */
  const handleConfirmDeleteStation = async (rowId?: string | null) => {
    if (!rowId) {
      setOpenStatus(false);
      setModalView({
        title: '',
        content: <> </>,
        header: true,
        rightCorner: '',
        notification: false,
      });
      dispatch(
        setNotificationMessage({ message: t('invalidStationId'), status: 400, type: 'error' }),
      );
      return;
    }

    try {
      await chargingStationApi.deleteDeviceStatus(rowId);

      // setSearchParams((params) => {
      //   params.delete('stationId');
      //   return params;
      // });
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters);
      setOpenStatus(true);
      showSuccessNotification();
    } catch (error: any) {
      setOpenStatus(false);
      setModalView({
        title: '',
        content: <> </>,
        header: true,
        rightCorner: '',
        notification: false,
      });
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || 'Something went wrong!',
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Delete Station API Call with Pre-check
   * @param rowId
   */
  const handleDeleteStation = async (rowId?: string | null) => {
    if (!rowId) {
      setOpenStatus(false);
      setModalView({
        title: '',
        content: <> </>,
        header: true,
        rightCorner: '',
        notification: false,
      });
      dispatch(
        setNotificationMessage({ message: t('invalidStationId'), status: 400, type: 'error' }),
      );
      return;
    }

    try {
      // Check if station can be deleted
      await chargingStationApi.getStationDeletionCheck(rowId);

      // If no conflict, delete directly
      await chargingStationApi.deleteDeviceStatus(rowId);
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      setSearchParameters(parameters);
      setOpenStatus(true);
      showSuccessNotification();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 409) {
        // Show confirmation dialog
        setOpenStatus(true);
        setModalView({
          title: '',
          header: false,
          rightCorner: '',
          notification: false,
          content: (
            <DeleteWarning
              selectedRowId={rowId}
              title={t('deleteStation')}
              description={t('deleteStationConflict')}
              cancelButtonLabel={t('cancel')}
              cancelButtonOnClick={closeModal}
              submitSvgIcon={<DeleteIcon />}
              submitButtonLabel={t('proceed')}
              submitButtonOnClick={() => handleConfirmDeleteStation(rowId)}
            />
          ),
        });
      } else if (status === 400) {
        // Cannot delete due to active charging sessions
        setOpenStatus(true);
        setModalView({
          title: '',
          header: false,
          rightCorner: '',
          notification: false,
          content: (
            <div className="delete-warning">
              <h4>{t('deleteWarning')}</h4>
              <div>
                <strong>{t('warningLabel')}!:</strong> {t('activeChargingWarning')}
              </div>
              <div>
                {t('cannotDeleteActiveStation')} <br />
                {t('waitForSessionCompletion')}
              </div>
            </div>
          ),
        });
      } else {
        setOpenStatus(false);
        setModalView({
          title: '',
          content: <> </>,
          header: true,
          rightCorner: '',
          notification: false,
        });
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || 'Something went wrong!',
            status: status || 500,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      }
    }
  };

  /**
   * Delete station warning popup
   */
  const handleDeleteStationWarning = (rowId?: string | null) => {
    setOpenStatus(true);
    setModalView({
      title: t('deleteStation'),
      header: false,
      notification: false,
      content: (
        <DeleteWarning
          selectedRowId={rowId}
          title={t('deleteStation')}
          description={t('deleteStationConfirm')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={closeModal}
          submitSvgIcon={<DeleteIcon />}
          submitButtonLabel={t('delete')}
          submitButtonOnClick={handleDeleteStation}
        />
      ),
      rightCorner: '',
    });
  };

  /**
   * Handle the tab change function
   * @param number
   */
  const changeTabPages = (number: number) => {
    setOpenTab(number);
    let pageName = '';
    switch (number) {
      case 0:
        pageName = 'approved';
        break;
      case 1:
        pageName = 'unapproved';
        break;
      case 2:
        pageName = 'rejected';
        break;

      default:
        break;
    }
    // navigate(`/ev-admin/charging-station/${pageName}`);
    // window.history.pushState({}, '', `${pageName}`);
    navigate(`../${pageName}`); // ✅ React Router method
  };
  useEffect(() => {
    if (currentPage) {
      let title = '';
      let count = 0;
      switch (currentPage) {
        case 'approved':
          title = t('chargingStation');
          count = totalResults;
          break;
        case 'unapproved':
          title = t('chargingStation');
          count = unapprovedTotalResults;
          break;
        case 'rejected':
          title = t('chargingStation');
          count = rejectedTotalResults;
          break;
        default:
          break;
      }
      dispatch(setPageTitle({ title, icon: <ChargingStationIcon />, count }));
    }
  }, [totalResults, unapprovedTotalResults, rejectedTotalResults]);
  useEffect(() => {
    if (currentPage) {
      let number = 0;
      switch (currentPage) {
        case 'approved':
          number = 0;
          break;
        case 'unapproved':
          number = 1;
          break;
        case 'rejected':
          number = 2;
          break;

        default:
          break;
      }
      setOpenTab(number);
    }
  }, []);
  useEffect(() => {
    dispatch(fetchPendingNotification());
    dispatch(fetchUnApprivedElectricPendingCount());
  }, []);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('chargingStation'),
        icon: <ChargingStationIcon />,
        count: totalResults,
      }),
    );
  }, [t]);
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <div className="container station-container">
        <div className="body-container">
          <div className="table-tab">
            <TabMenus
              openTab={openTab}
              setOpenTab={changeTabPages}
              menus={[
                {
                  name: t('approved'),
                },
                {
                  name: t('pending'),
                  count:
                    currentPages === 'station' ? pendingNotificationCount : pendingElectricityCount,
                },
                {
                  name: t('rejected'),
                },
              ]}
            />
          </div>
          <div className="table-content">
            {openTab === 0 && (
              <ApprovedListing
                viewFunction={viewFunction}
                currentPage={currentPages}
                setCurrentPage={setCurrentPages}
                handleDeleteStationWarning={handleDeleteStationWarning}
              />
            )}
            {openTab === 1 && (
              <UnApprovedListing
                viewFunction={viewFunction}
                currentPage={currentPages}
                setCurrentPage={setCurrentPages}
                handleDeleteStationWarning={handleDeleteStationWarning}
              />
            )}
            {openTab === 2 && (
              <RejectedListing
                viewFunction={viewFunction}
                currentPage={currentPages}
                setCurrentPage={setCurrentPages}
                handleDeleteStationWarning={handleDeleteStationWarning}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChargingStationManagement;
